import React, { useState } from 'react'
import {
  Box,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  VStack,
  Button,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  ButtonGroup,
  Image,
  Badge,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useGetGallery, useDeleteGallery } from 'queries/gallery.query'
import PropTypes from 'prop-types'
import { AiOutlineDelete } from 'react-icons/ai'
import { EditIcon } from '@chakra-ui/icons'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import Card from 'components/card/Card'
import { EditGalleryModal } from './EditGalleryModal'
import { DeleteGalleryModal } from './DeleteGalleryModal'
import { getStorageImageUrl } from 'utils/imageUrl'
import OptimizedImage from 'components/image/OptimizedImage'

const columnHelper = createColumnHelper()

export default function GalleryDashboard({ filterCategory }) {
  const { data: galleries, isLoading, isError, error, refetch } = useGetGallery()
  const { mutate: deleteMutate, isPending: isDeleting } = useDeleteGallery()
  
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')
  const toast = useToast()

  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()

  const [selectedItem, setSelectedItem] = useState(null)

  const filteredGalleries = React.useMemo(() => {
    if (!galleries) return []
    if (filterCategory === 'all') return galleries
    return galleries.filter(item => item.category?.toLowerCase() === filterCategory?.toLowerCase())
  }, [galleries, filterCategory])

  const handleEdit = (item) => {
    setSelectedItem(item)
    onEditOpen()
  }

  const handleDeleteOpen = (item) => {
    setSelectedItem(item)
    onDeleteOpen()
  }

  const handleDeleteSubmit = () => {
    if (!selectedItem) return
    deleteMutate(selectedItem.id, {
      onSuccess: () => {
        toast({ title: 'Item deleted.', status: 'success', duration: 3000, isClosable: true })
        onDeleteClose()
      },
      onError: err => {
        const backendMessage = err.response?.data?.message || err.response?.data?.error || err?.message || 'Failed to delete item.'
        toast({
          title: 'Deletion failed.',
          description: backendMessage,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
      }
    })
  }

  const columns = React.useMemo(() => [
    columnHelper.accessor('file_path', {
      id: 'file_path',
      header: () => <Text color='gray.400' fontSize='12px'>Image</Text>,
      cell: info => {
        const path = info.getValue()
        const src = getStorageImageUrl(path)
        return <OptimizedImage src={src} alt='Gallery' boxSize='50px' objectFit='cover' borderRadius='md' fallbackSrc='https://via.placeholder.com/50?text=No+Image' />
      },
    }),
    columnHelper.accessor('title', {
      id: 'title',
      header: () => <Text color='gray.400' fontSize='12px'>Title</Text>,
      cell: info => <Text color={textColor} fontSize='sm' fontWeight='bold'>{info.getValue() || 'Untitled Image'}</Text>,
    }),
    columnHelper.accessor('category', {
      id: 'category',
      header: () => <Text color='gray.400' fontSize='12px'>Category</Text>,
      cell: info => {
        const cat = info.getValue()?.toLowerCase()
        const color = cat === 'concert' ? 'purple' : cat === 'cultural' ? 'orange' : 'green'
        return (
          <Badge colorScheme={color} textTransform='uppercase'>
            {info.getValue() || 'concert'}
          </Badge>
        )
      },
    }),
    columnHelper.accessor('is_active', {
      id: 'is_active',
      header: () => <Text color='gray.400' fontSize='12px'>Status</Text>,
      cell: info => (
        <Badge colorScheme={info.getValue() ? 'green' : 'gray'}>
          {info.getValue() ? 'Active' : 'Inactive'}
        </Badge>
      ),
    }),
    columnHelper.accessor('created_at', {
      id: 'created_at',
      header: () => <Text color='gray.400' fontSize='12px'>Date Added</Text>,
      cell: info => <Text color={textColor} fontSize='sm'>{new Date(info.getValue()).toLocaleDateString()}</Text>,
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => <Text color='gray.400' fontSize='12px'>Actions</Text>,
      cell: info => (
        <ButtonGroup size='sm' isAttached variant='outline'>
          <Button onClick={() => handleEdit(info.row.original)}>
            <EditIcon color='blue.500' />
          </Button>
          <Button onClick={() => handleDeleteOpen(info.row.original)}>
            <AiOutlineDelete color='red' />
          </Button>
        </ButtonGroup>
      ),
    }),
  ], [textColor])

  const table = useReactTable({
    data: filteredGalleries,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (isLoading) {
    return <SkeletonTable />
  }

  if (isError) {
    return (
      <VStack spacing={4} align='center' mt='20'>
        <Alert status='error' variant='subtle'><AlertIcon /><Box><AlertTitle>Error Occurred!</AlertTitle><AlertDescription>{error.message}</AlertDescription></Box></Alert>
        <Button onClick={refetch} colorScheme='red'>Retry Loading</Button>
      </VStack>
    )
  }

  return (
    <Card direction='column' w='100%' px='0px' overflowX={{ sm: 'scroll', lg: 'hidden' }} mt={4}>
      {filteredGalleries.length === 0 ? (
        <Text color='gray.500' textAlign='center' py={10}>
          No {filterCategory !== 'all' ? `${filterCategory} ` : ''}gallery items found.
        </Text>
      ) : (
        <Box>
          <Table variant='simple' color='gray.500' mb='24px'>
            <Thead bg={TheadBackground}>
              {table.getHeaderGroups().map(headerGroup => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <Th key={header.id} borderColor={borderColor}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map(row => (
                <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }}>
                  {row.getVisibleCells().map(cell => (
                    <Td key={cell.id} borderColor={borderColor}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {selectedItem && (
        <EditGalleryModal isOpen={isEditOpen} onClose={onEditClose} item={selectedItem} />
      )}
      
      <DeleteGalleryModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onDelete={handleDeleteSubmit}
        isLoading={isDeleting}
      />
    </Card>
  )
}

GalleryDashboard.propTypes = {
  filterCategory: PropTypes.string,
}
